<template>
  <OverviewStandardEventStats
    @onEditMode="startEditMode"
    @onCancelChanges="cancelChanges"
    @onSaveTemplate="saveTemplate"
    @onRemovedWidget="onRemovedWidget"
    @onAddedWidget="onAddedWidget"
    @onDone="onMetricsDone"
    :editMode="editMode"
  />

  <div class="widget-container" data-step="3" :class="{ 'widgets--edit': editMode }">
    <div class="grid-stack">
      <div
        v-for="widgetItem in analyticsStore.chosenWidgets"
        class="grid-stack-item"
        :gs-x="widgetItem.x"
        :gs-y="widgetItem.y"
        :gs-w="widgetItem.w"
        :gs-h="widgetItem.h"
        :gs-id="widgetItem.id"
        :id="`widget-${widgetItem.id}`"
        :key="widgetItem.id"
      >
        <WidgetBase
          :widget="widgetItem.widget"
          :editMode="editMode"
          @onWidgetRemove="onRemovedWidget"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import mixpanel from '@/mixpanel.ts';

import { nextTick, ref } from 'vue';
import { useEcho } from '@laravel/echo-vue';
import { GridStack } from 'gridstack';
import { updateEventTemplate } from '@/utils/api';
import { useEventsStore } from '@/stores/events';
import { useAnalyticsStore } from '@/stores/analytics';
import { useUserStore } from '@/stores/user';
import { useToastStore } from '@/stores/toast';

import 'gridstack/dist/gridstack.min.css';
import 'gridstack/dist/gridstack-extra.min.css';

import WidgetBase from '@/components/widgets/WidgetBase.vue';
import OverviewStandardEventStats from '@/components/overview/StandardEventStats.vue';

const eventsStore = useEventsStore();
const analyticsStore = useAnalyticsStore();
const userStore = useUserStore();
const toast = useToastStore();
const editMode = ref(false);
const isGridReady = ref(false);

if (analyticsStore.chosenEvent?.id) {
  useEcho(
    `metrics.${analyticsStore.chosenEvent.id}`,
    '.metrics.private.event',
    (e: any) => {
      if (e?.message === 'metrics.updated') {
        analyticsStore.getEventWidgets(true);
      }
    },
  );
}

let grid: GridStack | null = null;

const setupGrid = () => {
  nextTick(() => {
    grid = GridStack.init({
      alwaysShowResizeHandle: true,
      column: 3,
      cellHeight: 275,
      margin: 8,
      float: false,
      handle: '.widget-move',
      disableResize: true,
      disableDrag: true,
    });

    grid.on('dragstop', function (event, element) {
      const node = element.gridstackNode;
      // TypeScript requires explicit null checks for optional properties
      if (node && node.id && node.x && node.y && node.w && node.h) {
        updateWidgetPlacement(node.id, node.x, node.y, node.w, node.h);
      }
    });

    grid.on('resizestop', function (event, element) {
      const node = element.gridstackNode;
      // This doesn't seem to fire but I have no idea if that matters? Maybe not?
      if (node && node.id && node.x && node.y && node.w && node.h) {
        updateWidgetPlacement(node.id, node.x, node.y, node.w, node.h);
      }
    });

    isGridReady.value = true;
  });
};

eventsStore.getEventListData();
setupGrid();

const startEditMode = () => {
  if (analyticsStore.chosenEvent?.id) {
    mixpanel.track('Edit Event Layout', {
      'Event Name': analyticsStore.chosenEvent?.name,
      'Event UUID': analyticsStore.chosenEvent.uuid,
      'Event ID': analyticsStore.chosenEvent.id,
    });
  }
  editMode.value = true;
  if (grid) {
    grid.opts.disableDrag = false;
    grid.opts.disableResize = false;
    grid.resizable('.grid-stack-item', true);
  }
};

const prepWidgets = () => {
  // Update the position, size, and order for all widgets.
  const domWidgets = grid?.engine.nodes;

  if (domWidgets && domWidgets.length) {
    const widgetsToSave = domWidgets.map((widget) => {
      return {
        id: widget.id,
        widget_id: widget.id,
        position: {
          h: widget.h,
          w: widget.w,
          x: widget.x,
          y: widget.y,
        },
      };
    });

    return widgetsToSave;
  }

  return [];
};

const saveTemplate = () => {
  const widgetsToSave = prepWidgets();
  const eventId = analyticsStore.chosenEvent?.id;
  const templateId = analyticsStore.chosenEventDetails?.template_id;

  const payload = {
    widgets: widgetsToSave,
  };

  if (eventId && templateId) {
    updateEventTemplate(userStore.accessToken, eventId, templateId, payload)
      .then((resp) => {
        if (toast.success(resp)) {
          editMode.value = false;
          if (grid) {
            grid.opts.disableResize = true;
            grid.opts.disableDrag = true;
            grid.resizable('.grid-stack-item', false);
          }
          if (analyticsStore.chosenEventDetails) {
            analyticsStore.chosenEventDetails.template_id = resp.data.id;
          }
        }
      })
      .catch((error) => {
        toast.error(error);
      });
  }
};

const cancelChanges = async () => {
  grid?.removeAll(true);
  await analyticsStore.getEventWidgets();
  setupGrid();
  editMode.value = false;
};

// Update the Widget properties to match the new size/placement for when we save it.
const updateWidgetPlacement = (id: string, x: number, y: number, w: number, h: number) => {
  const widget = analyticsStore.chosenWidgets.find((widget) => widget.id === parseInt(id));
  if (widget) {
    widget.x = x;
    widget.y = y;
    widget.h = h;
    widget.w = w;
    widget.widget.position.x = x;
    widget.widget.position.y = y;
    widget.widget.position.w = w;
    widget.widget.position.h = h;
  }
};

const onRemovedWidget = (widgetId: number) => {
  if (grid) {
    const selector = `#widget-${widgetId}`;
    grid.removeWidget(selector, false);

    analyticsStore.chosenWidgets = analyticsStore.chosenWidgets.filter((widget) => {
      return widget.id !== widgetId;
    });
  }
};

const onAddedWidget = (newWidgetId: number) => {
  if (grid && isGridReady.value) {
    grid.makeWidget(`#widget-${newWidgetId}`);
  }
};

const onMetricsDone = () => {
  if (grid) {
    grid.compact('list');
  }
};

// Allow these to be triggered by the parent
defineExpose({
  onRemovedWidget,
  onAddedWidget,
  saveTemplate,
});
</script>

<style lang="scss">
.widget-container {
  border: 4px solid transparent;
  border-radius: variables.$sizing-xxs;

  &.widgets--edit {
    border-color: variables.$primitives-color-primary-100;
  }
}
</style>
